// The "Measured this weekend" table: from /api/data/basis when the worker has samples in the frozen
// window, else from the recorded snapshot.
import type { BasisPayload, BasisRow } from '../api/types';
import { dayName, fmtUsd, fmtUtc, parseTs, snapDay } from './format';
import type { Snapshot } from './snapshot';

export const BASIS_INTRO = 'Pool price against the frozen feed, in percent, for 15 Stock Tokens. First and last reading of our recording window.';
export const BASIS_HEADS: readonly [string, string] = ['Sunday 22:00 UTC', 'Monday 14:00 UTC'];

export interface BasisTableRow { key: string; sym: string; first: number | null; last: number | null; liquidity: string; moved: string }
export interface BasisTableModel { heads: readonly [string, string]; intro: string; rows: BasisTableRow[] }

export const snapNote = (snap: Snapshot): string =>
  `recorded snapshot ${snapDay(snap.meta.recorded_from)} to ${snapDay(snap.meta.recorded_to)} UTC`;

/** Recorded snapshot (data/basis-snapshot.json); "note" is appended to the intro when the API is down. */
export function snapshotTable(snap: Snapshot, note: string | null): BasisTableModel {
  const order = Object.entries(snap.series).sort((a, b) => b[1].last_basis - a[1].last_basis);
  return {
    heads: BASIS_HEADS,
    intro: BASIS_INTRO + (note ? ` ${note}` : ''),
    rows: order.map(([sym, s]) => ({
      key: sym,
      sym,
      first: s.points[0]?.basis ?? null,
      last: s.last_basis,
      liquidity: `${fmtUsd(s.liquidity_usd, 0)} USD`,
      moved: fmtUtc(new Date(s.oracle_updated_at)),
    })),
  };
}

export type ApiTable = BasisTableModel | { empty: true; note: (snap: Snapshot) => string };

/** Live table from /api/data/basis (sorted by last_basis by the worker). When the worker has no sample
 *  yet inside the window the result carries the note for the snapshot table instead. */
export function apiTable(b: BasisPayload): ApiTable {
  const rows: BasisRow[] = Array.isArray(b.rows) ? b.rows : [];
  const from = parseTs(b.window.from), to = parseTs(b.window.to);
  const reason = b.window.reason ? b.window.reason.replace('holiday:', 'holiday, ') : 'frozen window';
  const span = from && to ? `${dayName(from)} ${fmtUtc(from)} to ${dayName(to)} ${fmtUtc(to)}` : 'the current frozen window';
  if (rows.length === 0) {
    return { empty: true, note: (snap) => `The worker has no sample yet inside the window ${span} (${reason}); ${snapNote(snap)}.` };
  }
  const rf = parseTs(b.recorded_from), rt = parseTs(b.recorded_to);
  return {
    heads: [rf ? `First, ${fmtUtc(rf)}` : 'First reading', rt ? `Last, ${fmtUtc(rt)}` : 'Last reading'],
    intro: `Pool price against the frozen feed, in percent, for ${rows.length} Stock Tokens. First and last reading of the worker's samples in the frozen window ${span} (${reason})${b.window.state === 'frozen' ? ', still open' : ''}.`,
    rows: rows.map((r) => {
      const at = parseTs(r.oracle_updated_at);
      return {
        key: r.symbol,
        sym: r.symbol,
        first: r.first_basis,
        last: r.last_basis,
        liquidity: r.liquidity_usd != null ? `${fmtUsd(r.liquidity_usd, 0)} USD` : 'n/a',
        moved: at ? fmtUtc(at) : 'n/a',
      };
    }),
  };
}
